import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  findTrailById,
  validatePointIndex,
  parseCurrentURLLocation,
} from "Utils/tourNavigation";
import {
  navigateToTrail,
  resetCurrentTrail,
} from "Containers/Trails/TrailsActions";
import {
  setCurrentStop,
  stopChangeTrigger,
} from "Containers/Stops/currentStop.Actions";
import { setStopState } from "Containers/Stops/StopActions";
import StopState from "Containers/Stops/StopState";
import { setView, panToStop } from "Containers/CampusMap";
import { setSelectedDestinations } from "Containers/Destinations";
import {
  setStartingPoint,
  setDestination,
} from "Containers/Directions/DirectionsActions";
import { IRootState } from "../../store/rootState";

interface ParsedLocation {
  trailId: string;
  pointIndex: number;
  fromView?: string;
  startStopId?: string;
  endStopId?: string;
  isDirections?: boolean;
  stopId?: number;
  isStandalone?: boolean;
}

/**
 * Custom hook to handle tour navigation from URL parameters
 * This will navigate to a specific trail and point when the data is loaded
 */
export const useTourNavigation = () => {
  const dispatch = useDispatch();
  const tourModel = useSelector((state: IRootState) => state.tour);
  const trails = useSelector(
    (state: IRootState) => state.trails?.suggestedTrails
  );
  const stops = useSelector((state: IRootState) => state.stops);

  // Use a ref to track if we've already navigated to prevent infinite loops
  const hasNavigated = useRef(false);

  const [parsedLocation, setParsedLocation] = useState<ParsedLocation | null>(
    parseCurrentURLLocation()
  );

  // The parsed location is set once on component mount from the current URL

  // Create a stable string key for the parsed location to use in dependency array
  const locationKey = parsedLocation
    ? parsedLocation.isDirections
      ? `${parsedLocation.startStopId}:${parsedLocation.endStopId}:r`
      : parsedLocation.isStandalone
      ? `${parsedLocation.stopId}:0:s`
      : `${parsedLocation.trailId}:${parsedLocation.pointIndex}`
    : null;

  // Combine all loading states into one computed value to prevent race conditions
  const isDataReady = useMemo(() => {
    return (
      !tourModel?.isLoading && // Tour data loaded
      trails &&
      trails.length > 0 && // Trails available
      stops &&
      stops.length > 0 // Stops available
    );
  }, [tourModel?.isLoading, trails, stops]);

  // Reset navigation flag when location changes
  useEffect(() => {
    hasNavigated.current = false;
  }, [locationKey]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const currentLocation = parseCurrentURLLocation();
      setParsedLocation(currentLocation);
      hasNavigated.current = false;
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    // If we've already navigated, don't do it again
    if (hasNavigated.current) {
      return;
    }

    if (!parsedLocation) {
      return;
    }

    // Wait for all data to be ready to prevent race conditions
    if (!isDataReady) {
      return;
    }

    // Handle directions navigation differently
    if (
      parsedLocation.isDirections &&
      parsedLocation.startStopId &&
      parsedLocation.endStopId
    ) {
      // Reset trail state when navigating to directions
      dispatch(resetCurrentTrail());
      dispatch(setView("directions"));

      // Find stops by their IDs
      const startStop = stops.find(
        (stop) =>
          stop.id === parseInt(parsedLocation.startStopId!, 10) ||
          stop.id === parsedLocation.startStopId
      );
      const endStop = stops.find(
        (stop) =>
          stop.id === parseInt(parsedLocation.endStopId!, 10) ||
          stop.id === parsedLocation.endStopId
      );

      if (startStop && endStop) {
        dispatch(
          setStartingPoint({
            id: startStop.id,
            title: startStop.title,
            realLat: startStop.realLat,
            realLng: startStop.realLng,
            customLat: startStop.customLat,
            customLng: startStop.customLng,
          })
        );
        dispatch(
          setDestination({
            id: endStop.id,
            title: endStop.title,
            realLat: endStop.realLat,
            realLng: endStop.realLng,
            customLat: endStop.customLat,
            customLng: endStop.customLng,
          })
        );
      }

      hasNavigated.current = true;
      return;
    }

    // Handle standalone stops
    if (parsedLocation.isStandalone && parsedLocation.stopId !== undefined) {
      // Reset trail state when navigating to standalone stop
      dispatch(resetCurrentTrail());

      // Navigate directly to the standalone stop
      dispatch(
        setCurrentStop(parsedLocation.stopId, stopChangeTrigger.QUERY_PARAM)
      );
      dispatch(setStopState([parsedLocation.stopId], StopState.Selected));

      // Pan to the standalone stop
      dispatch(panToStop(parsedLocation.stopId));

      hasNavigated.current = true;
      return;
    }

    const { trailId, pointIndex, fromView } = parsedLocation;

    // Find the trail by ID
    const trailMatch = findTrailById(trails, trailId);

    if (!trailMatch) {
      return;
    }

    // Dispatch navigation to trail
    dispatch(navigateToTrail(trailId, trailMatch.trailIndex));

    // Set the appropriate view based on the URL parameter
    if (fromView === "trails") {
      dispatch(setView("trails"));
    } else if (fromView === "destinations") {
      dispatch(setView("destinations"));
    }

    // Get the actual stops for this trail
    const trailStops = trailMatch.trail.Stops || [];

    // Validate point index against actual trail stops
    const validPointIndex = validatePointIndex(trailStops, pointIndex);

    // Get the actual stop ID from the validated index
    let stopId;
    if (trailStops[validPointIndex]) {
      // Convert stopid to number since setCurrentStop expects a number
      const stopIdStr =
        trailStops[validPointIndex].stopid || trailStops[validPointIndex].id;
      stopId = parseInt(stopIdStr, 10);
    } else {
      // Fallback to using the index itself
      stopId = validPointIndex;
    }

    // Dispatch navigation to point/stop with appropriate trigger based on view
    const trigger =
      fromView === "destinations"
        ? stopChangeTrigger.DESTINATION_LIST
        : stopChangeTrigger.QUERY_PARAM;
    dispatch(setCurrentStop(stopId, trigger));

    // Also set the stop state to selected to highlight it on the map
    dispatch(setStopState([stopId], StopState.Selected));

    // For destinations view, also set selected destinations to trigger map panning
    if (fromView === "destinations" && trailStops[validPointIndex]) {
      const stop = trailStops[validPointIndex];
      dispatch(setSelectedDestinations(stop.photoThumb || stop.panoThumb));
    }

    // Trigger map panning to the stop
    dispatch(panToStop(stopId));

    // Mark that we've successfully navigated to prevent infinite loops
    hasNavigated.current = true;
  }, [dispatch, locationKey, parsedLocation, isDataReady, trails, stops]);
};

export default useTourNavigation;
